import type { TicketStatusCondition as TicketStatusConditionType } from "@redotech/redo-flows-common/rules/schemas";
import { TicketStatusOption } from "@redotech/redo-flows-common/rules/schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const statusDisplayNames = (): Record<TicketStatusOption, string> => ({
  [TicketStatusOption.OPEN]: t("Open", { context: "support ticket status" }),
  [TicketStatusOption.CLOSED]: t("Closed", {
    context: "support ticket status",
  }),
  [TicketStatusOption.SNOOZED]: t("Snoozed", {
    context: "support ticket status",
  }),
  [TicketStatusOption.IN_PROGRESS]: t("In progress", {
    context: "support ticket status",
  }),
});

const statusItems = (): RedoListItem<TicketStatusOption>[] =>
  Object.values(TicketStatusOption).map((status) => ({
    id: status,
    type: "text" as const,
    text: statusDisplayNames()[status],
    value: status,
  }));

export const TicketStatusCondition = memo(function TicketStatusCondition({
  condition,
  onChange,
}: {
  condition: TicketStatusConditionType;
  onChange: (updated: TicketStatusConditionType) => void;
}) {
  const { t } = useTranslation();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<TicketStatusOption>[] = useMemo(() => {
    return (condition.statuses || []).map((status) => ({
      id: status,
      type: "text" as const,
      text: statusDisplayNames()[status],
      value: status,
    }));
  }, [condition.statuses]);

  const displayItem: RedoListItem<TicketStatusOption> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: t("{{count}} statuses selected", { count: selectedItems.length }),
        value: TicketStatusOption.OPEN,
      };
    }, [selectedItems, t]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Status", { context: "support ticket status" })}
      </Text>
      <RedoSingleSelectDropdownInputButton
        isDropdownOpen={dropdownOpen}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        placeholder={t("Select statuses")}
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={statusItems()}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
